import type { ReactNode } from 'react';
import { useMemo } from 'react';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Banners } from '@sorare/core/src/components/layout/Banners';
import { MobileAppBottomPortalReceiver } from '@sorare/core/src/components/navigation/AppNavigation';
import {
  sportAnalyticTag,
  useSportContext,
} from '@sorare/core/src/contexts/sport';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { LIFECYCLE } from '@sorare/core/src/hooks/useLifecycle';
import { useTitleAndDescription } from '@sorare/core/src/hooks/useTitleAndDescription';
import { useTour } from '@sorare/core/src/hooks/useTour';
import { createCardSorts } from '@sorare/core/src/lib/algolia';
import { metadatas } from '@sorare/core/src/lib/seo/football';

import { NewsSignings } from '#src/components/market/OnboardingDialog/NewsSignings/index.tsx';
import { PageTemplate } from '#src/components/market/PageTemplate/index.tsx';
import { useDefaultFilters } from '#src/hooks/useDefaultFilters.ts';
import { primaryMarketFilters as footballFilters } from '#src/searchCards/football/cardFilters.tsx';
import { AdvancedCardSearch } from '#src/searchCards/index.ts';
import { primaryMarketFilters as baseballFilters } from '#src/searchCards/mlb/cardFilters.tsx';
import { primaryMarketFilters as nbaFilters } from '#src/searchCards/nba/cardFilters.tsx';

const primaryMarketFilters = {
  [Sport.FOOTBALL]: () => footballFilters,
  [Sport.BASEBALL]: baseballFilters,
  [Sport.NBA]: () => nbaFilters,
};

type Props = {
  banner: ReactNode;
};

export const Auctions = ({ banner }: Props) => {
  const { sport } = useSportContext();
  const {
    flags: { useMlbSeasonAverageScore = true },
  } = useFeatureFlags();
  const { isIosApp, isMarketAuthorizedIosApp } = useIsMobileApp();

  useTitleAndDescription(
    metadatas.newSignings.title,
    metadatas.newSignings.description
  );

  const filters = useDefaultFilters({
    auction: true,
  });

  const cardSorts = useMemo(
    () =>
      createCardSorts({
        indexPrefix: 'Cards On Sale',
        withHighestAverageScore: true,
      }),
    []
  );

  const defaultSort = 'Cards On Sale Ending Soon';

  const cardsFilter = useMemo(
    () => primaryMarketFilters[sport]?.(useMlbSeasonAverageScore),
    [sport, useMlbSeasonAverageScore]
  );
  const filtersTour = useTour(LIFECYCLE.newSignings, 'filters');
  const cardsTour = useTour(LIFECYCLE.newSignings, 'cards');
  return (
    <>
      {!!(!isIosApp || isMarketAuthorizedIosApp) && (
        <>
          <NewsSignings />
          <Banners />
        </>
      )}
      <PageTemplate showBack>
        <AdvancedCardSearch
          sport={sport}
          sorts={cardSorts}
          defaultSort={defaultSort}
          cardFilters={cardsFilter}
          defaultFilters={filters}
          analyticsTags={['NewSignings', sportAnalyticTag[sport]]}
          onlyPrimary
          removeFinishedAuctions
          hideSorareUser
          banner={banner}
          {...(sport !== Sport.FOOTBALL && {
            filtersTour,
            cardsTour,
          })}
          distinct
        />
      </PageTemplate>
      {!!isIosApp && (
        <MobileAppBottomPortalReceiver id="above-bottom-bar-portal" />
      )}
    </>
  );
};
